import { useState } from "react";
import { ArrowRight, Check } from "lucide-react";
import { softPost } from "@/lib/fitkey";
import { authRequest } from "@/lib/session";

export const VERDICTS = [
  { key: "too_small", label: "Too small" },
  { key: "perfect", label: "Perfect" },
  { key: "too_large", label: "Too large" },
];

export const RETURN_REASONS = [
  { key: "too_small", label: "Too small" },
  { key: "too_large", label: "Too large" },
  { key: "not_as_expected", label: "Not as expected" },
  { key: "other", label: "Other" },
];

const labelCls = "text-[10.5px] uppercase tracking-[0.2em] text-neutral-500";
const inputCls =
  "mt-1.5 w-full rounded-xl border border-black/10 bg-white px-3.5 h-11 text-[14px] text-[#0A0A0A] placeholder:text-neutral-300 outline-none focus:border-black/50 transition-colors";

export function SoftDone({ children, testid }) {
  return (
    <div
      data-testid={testid}
      className="mt-4 flex items-start gap-2.5 rounded-xl bg-neutral-50 border border-black/5 px-4 py-3.5 text-[13px] text-neutral-700"
    >
      <span className="mt-0.5 grid place-items-center h-4 w-4 rounded-full bg-[#0A0A0A] shrink-0">
        <Check className="h-2.5 w-2.5 text-white" />
      </span>
      {children}
    </div>
  );
}

const STATUS_STYLE = {
  requested: "bg-amber-50 text-amber-800 border-amber-200/70",
  approved: "bg-sky-50 text-sky-800 border-sky-200/70",
  received: "bg-sky-50 text-sky-800 border-sky-200/70",
  refunded: "bg-emerald-50 text-emerald-800 border-emerald-200/70",
  completed: "bg-emerald-50 text-emerald-800 border-emerald-200/70",
  rejected: "bg-red-50 text-red-700 border-red-200/70",
  perfect: "bg-emerald-50 text-emerald-800 border-emerald-200/70",
  too_small: "bg-amber-50 text-amber-800 border-amber-200/70",
  too_large: "bg-amber-50 text-amber-800 border-amber-200/70",
};

const STATUS_LABEL = { too_small: "Too small", too_large: "Too large", perfect: "Perfect fit" };

export function StatusChip({ status, testid }) {
  const key = String(status || "").toLowerCase();
  const label = STATUS_LABEL[key] || (key ? key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ") : "Unknown");
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] ${
        STATUS_STYLE[key] || "bg-neutral-50 text-neutral-600 border-black/10"
      }`}
    >
      {label}
    </span>
  );
}

// "Did it fit?" -> POST /api/fit-feedback (public, by fitkey_id).
// `editablePair` lets the user name the pair they bought (My FitKey area).
export function ConfirmFitForm({
  fitkeyId,
  brand = "",
  model = "",
  sizeRecommended,
  editablePair = false,
  testid = "fitkey-confirm",
  onDone,
}) {
  const [brandIn, setBrandIn] = useState(brand);
  const [modelIn, setModelIn] = useState(model);
  const [verdict, setVerdict] = useState(null);
  const [sizePurchased, setSizePurchased] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | done
  const [error, setError] = useState("");

  const send = async () => {
    const b = (editablePair ? brandIn : brand).trim();
    if (!verdict) return;
    if (!b) {
      setError("Add the brand of the pair first.");
      return;
    }
    setError("");
    setState("sending");
    const res = await softPost("/fit-feedback", {
      fitkey_id: fitkeyId,
      brand: b,
      model: (editablePair ? modelIn : model).trim() || undefined,
      size_recommended: sizeRecommended || undefined,
      size_purchased: sizePurchased.trim() || undefined,
      fit_verdict: verdict,
    });
    if (res.ok) {
      setState("done");
      onDone?.(res.data);
    } else {
      setState("idle");
      setError(
        res.status === 404 ? "We couldn't find this FitKey." : "Couldn't save your fit right now. Please try again."
      );
    }
  };

  if (state === "done") {
    return <SoftDone testid={`${testid}-done`}>Thanks, your fit is recorded. Your next size gets sharper.</SoftDone>;
  }

  return (
    <div data-testid={`${testid}-form`}>
      {editablePair && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Brand</label>
            <input
              data-testid={`${testid}-brand`}
              value={brandIn}
              onChange={(e) => setBrandIn(e.target.value)}
              placeholder="Brand"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Model</label>
            <input
              data-testid={`${testid}-model`}
              value={modelIn}
              onChange={(e) => setModelIn(e.target.value)}
              placeholder="Model"
              className={inputCls}
            />
          </div>
        </div>
      )}
      <div className="mt-4 grid grid-cols-3 gap-2">
        {VERDICTS.map((v) => (
          <button
            key={v.key}
            type="button"
            data-testid={`${testid}-${v.key}`}
            aria-pressed={verdict === v.key}
            onClick={() => setVerdict(v.key)}
            className={`rounded-xl border px-2 py-3 text-[13px] transition-all ${
              verdict === v.key ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-black/10 hover:bg-neutral-50"
            }`}
          >
            {v.label}
          </button>
        ))}
      </div>
      <div className="mt-4 flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex-1">
          <label className={labelCls}>
            Size you bought <span className="normal-case tracking-normal text-neutral-400">(optional)</span>
          </label>
          <input
            data-testid={`${testid}-size-purchased`}
            inputMode="decimal"
            value={sizePurchased}
            onChange={(e) => setSizePurchased(e.target.value)}
            placeholder="e.g. EU 42½"
            className={inputCls}
          />
        </div>
        <button
          type="button"
          disabled={!verdict || state === "sending"}
          data-testid={`${testid}-submit`}
          onClick={send}
          className="group inline-flex items-center justify-center gap-1.5 rounded-full bg-[#0A0A0A] text-white h-11 px-5 text-[13px] hover:bg-neutral-800 transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {state === "sending" ? "Saving" : "Confirm fit"}
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
      {error && (
        <div data-testid={`${testid}-error`} className="mt-2 text-[12px] text-red-600">
          {error}
        </div>
      )}
    </div>
  );
}

// "Request a return" -> POST /api/return (signed in; Bearer token).
export function ReturnForm({ fitkeyId, testid = "fitkey-return", onDone }) {
  const [reason, setReason] = useState(null);
  const [note, setNote] = useState("");
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const send = async () => {
    if (!reason) return;
    setState("sending");
    setError("");
    const label = RETURN_REASONS.find((r) => r.key === reason)?.label || reason;
    try {
      const data = await authRequest("post", "/return", {
        fitkey_id: fitkeyId,
        reason: note.trim() ? `${label}: ${note.trim()}` : label,
      });
      setState("done");
      onDone?.(data);
    } catch (e) {
      setState("idle");
      if (e.status === 401) return; // redirected to /signin
      setError(
        e.status === 403
          ? "You're signed in with a different FitKey."
          : "Couldn't send your return request right now. Please try again."
      );
    }
  };

  if (state === "done") {
    return (
      <SoftDone testid={`${testid}-done`}>
        Return requested. Your Wallet pass now shows the status, and next steps will appear in My FitKey.
      </SoftDone>
    );
  }

  return (
    <div data-testid={`${testid}-form`}>
      <p className="mt-1.5 text-[13px] text-neutral-500">Tell us why, it improves your next size.</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {RETURN_REASONS.map((r) => (
          <button
            key={r.key}
            type="button"
            data-testid={`${testid}-reason-${r.key}`}
            aria-pressed={reason === r.key}
            onClick={() => setReason(r.key)}
            className={`rounded-full border px-3.5 py-2 text-[12.5px] transition-all ${
              reason === r.key ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-black/10 hover:bg-neutral-50"
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>
      <textarea
        data-testid={`${testid}-note`}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={2}
        maxLength={300}
        placeholder="Anything else? (optional)"
        className="mt-3 w-full rounded-xl border border-black/10 bg-white px-3.5 py-2.5 text-[14px] placeholder:text-neutral-300 outline-none focus:border-black/50 resize-none"
      />
      <button
        type="button"
        disabled={!reason || state === "sending"}
        data-testid={`${testid}-submit`}
        onClick={send}
        className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-full border border-black/15 bg-white h-11 text-[13px] hover:bg-neutral-50 transition-all active:scale-[0.99] disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {state === "sending" ? "Sending" : "Request return"}
      </button>
      {error && (
        <div data-testid={`${testid}-error`} className="mt-2 text-[12px] text-red-600">
          {error}
        </div>
      )}
    </div>
  );
}
